import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { InviteMemberInput, MemberRole, Plan } from '@guabi/shared';
import { apiDelete, apiGet, apiPatch, apiPost } from '@/lib/api';
import { meQueryKey } from '@/features/shell/queries';

export interface MemberRow {
  userId: string;
  name?: string;
  email: string;
  role: MemberRole;
  /** Unset while an invited person has not signed in yet. */
  acceptedAt?: string;
}

export interface InviteRow {
  email: string;
  name?: string;
  role: MemberRole;
  /** Unix seconds. */
  expiresAt: number;
}

export interface MembersResponse {
  members: MemberRow[];
  invites: InviteRow[];
  callerRole: MemberRole;
  plan: Plan;
}

export interface InviteResult {
  status: 'member' | 'invited';
  /** Whether Cognito emailed a temporary password, so the UI can say what happens next. */
  provisioned: 'created' | 'exists' | 'skipped' | 'failed';
}

export const membersQueryKey = (wsId: string) => ['workspace', wsId, 'members'] as const;

export function useMembers(wsId: string | undefined) {
  return useQuery({
    queryKey: membersQueryKey(wsId ?? ''),
    queryFn: () => apiGet<MembersResponse>(`/workspaces/${wsId}/members`),
    enabled: Boolean(wsId),
  });
}

/** Every mutation invalidates the members list, and `me` because roles live there too. */
function useMemberMutation<TArgs, TResult = void>(
  fn: (args: TArgs) => Promise<TResult>,
  wsId: string,
) {
  const qc = useQueryClient();
  return useMutation<TResult, Error, TArgs>({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: membersQueryKey(wsId) });
      void qc.invalidateQueries({ queryKey: meQueryKey });
    },
  });
}

/** The role an invite may carry: anything but `owner`, which the schema refines out. */
export type InvitableRole = InviteMemberInput['role'];

export function useInviteMember(wsId: string) {
  return useMemberMutation<InviteMemberInput, InviteResult>(
    (input) => apiPost<InviteResult>(`/workspaces/${wsId}/members`, input),
    wsId,
  );
}

export function useUpdateMember(wsId: string) {
  return useMemberMutation<{ userId: string; role: MemberRole }>(
    ({ userId, role }) => apiPatch(`/workspaces/${wsId}/members/${userId}`, { role }),
    wsId,
  );
}

export function useRemoveMember(wsId: string) {
  return useMemberMutation<{ userId: string }>(
    ({ userId }) => apiDelete(`/workspaces/${wsId}/members/${userId}`),
    wsId,
  );
}

export function useRevokeInvite(wsId: string) {
  return useMemberMutation<{ email: string }>(
    ({ email }) => apiDelete(`/workspaces/${wsId}/invites/${encodeURIComponent(email)}`),
    wsId,
  );
}
